import { WeatherIcon } from '@/lib/icons'
import type { CalendarTask, CalendarLog } from '@/types/calendar'
import type { WeatherDaily } from '@/types/weather'
import type { Overlays } from './OverlayToggles'

interface DayCellProps {
  date: string // "YYYY-MM-DD"
  dayNumber: number
  isCurrentMonth: boolean
  isToday: boolean
  tasks: CalendarTask[]
  logs: CalendarLog[]
  weather: WeatherDaily | undefined
  overlays: Overlays
  isSelected: boolean
  onClick: (date: string) => void
}

const MAX_DOTS = 6

export function DayCell({
  date, dayNumber, isCurrentMonth, isToday,
  tasks, logs, weather, overlays, isSelected, onClick,
}: DayCellProps) {
  const hasOverdue = overlays.tasks && tasks.some(t => t.overdue)
  const taskDots = overlays.tasks ? tasks : []
  const logDots = overlays.completed ? logs : []
  const totalDots = taskDots.length + logDots.length
  const overflow = totalDots > MAX_DOTS ? totalDots - MAX_DOTS : 0
  const shownTaskDots = taskDots.slice(0, MAX_DOTS)
  const shownLogDots = logDots.slice(0, Math.max(0, MAX_DOTS - shownTaskDots.length))

  let bgClass = 'bg-paper'
  if (isSelected) bgClass = 'bg-botanical-light'
  else if (isToday) bgClass = 'bg-gold/10'
  else if (hasOverdue) bgClass = 'bg-danger/5'

  let borderClass = 'border border-frame/10'
  if (isSelected) borderClass = 'border-2 border-botanical'
  else if (isToday) borderClass = 'border-2 border-gold'

  return (
    <button
      onClick={() => onClick(date)}
      className={`relative min-h-[4.5rem] md:min-h-[5.5rem] p-xs text-left transition-colors hover:bg-paper-dark rounded-sm ${bgClass} ${borderClass} ${!isCurrentMonth ? 'opacity-40' : ''}`}
    >
      <div className="flex items-baseline justify-between">
        <span className={`font-label text-sm ${isToday ? 'font-bold text-gold' : ''}`}>
          {dayNumber}
        </span>
        {overlays.weather && weather && (
          <span className="flex items-center gap-[3px]">
            <WeatherIcon code={weather.weather_code} size={18} />
            <span className="font-label text-xs text-frame">{Math.round(weather.temp_max)}°</span>
          </span>
        )}
      </div>
      {(shownTaskDots.length > 0 || shownLogDots.length > 0) && (
        <div className="absolute bottom-xs left-xs flex flex-wrap gap-[3px] max-w-[calc(100%-0.5rem)]">
          {shownTaskDots.map(t => (
            <span
              key={`t-${t.id}`}
              className={`w-[6px] h-[6px] rounded-full ${t.overdue ? 'bg-danger' : 'bg-botanical'}`}
              title={`${t.task_type_pl} — ${t.plant_name}`}
            />
          ))}
          {shownLogDots.map(l => (
            <span
              key={`l-${l.id}`}
              className="w-[6px] h-[6px] rounded-full bg-gold opacity-60"
              title={`${l.action_pl} — ${l.plant_name}`}
            />
          ))}
          {overflow > 0 && (
            <span className="font-label text-[8px] text-frame/60 leading-none">+{overflow}</span>
          )}
        </div>
      )}
    </button>
  )
}
